import { useState } from 'react';
import { Box, Container, Typography } from '@mui/material';
import type { LedgerCollapse } from '../../../shared/components/ledger';
import { ActivityLedgerDemo } from './ActivityLedgerDemo';
import { PotLedgerDemo } from './PotLedgerDemo';
import { ScreenFrame } from './ScreenFrame';
import { SiteLedgerDemo } from './SiteLedgerDemo';

/**
 * /demo — a harness for the shared ledger layer
 * (`shared/components/ledger/`), built from the exported components with fake
 * data in the shape of the three screens that render booked work. Each frame
 * owns its own CSS container, so the width toggles exercise the real collapse
 * behaviour. Deleted once the three screens have rolled over.
 */

const SITE_COLLAPSE: LedgerCollapse = { container: 'site-demo', at: 620 };
const POT_COLLAPSE: LedgerCollapse = { container: 'pot-demo', at: 620 };
const ACTIVITY_COLLAPSE: LedgerCollapse = {
  container: 'activity-demo',
  at: 760,
};

export function ListMockupsPage() {
  const [opened, setOpened] = useState<string | null>(null);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Booked-work ledger
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1, maxWidth: '72ch' }}
      >
        One shared ledger anatomy across the three booked-work screens. Status
        lives in the leading dot, part-booked items render each booking as a
        peer row capped by a subtotal strip, unbooked items read as the plot
        template. Hover a title for the card, right-click a booking for the
        cancel menu, and use the width toggles to preview tablet / phone
        collapse. All data is fake.
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 4 }}
      >
        {opened ? `Row activated: ${opened}` : 'Click or press Enter on a row.'}
      </Typography>

      <ScreenFrame
        container={SITE_COLLAPSE.container}
        title="Site view · Kiddlington TF01, Plots tab"
        subtitle="Progress across the site: plots → labour items (booked, part-booked, and unbooked template items) → bookings, plus day work"
      >
        <SiteLedgerDemo collapse={SITE_COLLAPSE} onOpen={setOpened} />
      </ScreenFrame>

      <ScreenFrame
        container={POT_COLLAPSE.container}
        title="Pot breakdown · Romas Kvedys, Wk 30"
        subtitle="What the payer reviews: this pot's bookings, day work and deductions, grouped by site · plot"
      >
        <PotLedgerDemo collapse={POT_COLLAPSE} onOpen={setOpened} />
      </ScreenFrame>

      <ScreenFrame
        container={ACTIVITY_COLLAPSE.container}
        title="Activity · all sites, weekly"
        subtitle="Admin review: weeks → every booking org-wide, with site / plot context"
      >
        <ActivityLedgerDemo collapse={ACTIVITY_COLLAPSE} onOpen={setOpened} />
      </ScreenFrame>

      <Box sx={{ mt: 2, pb: 6 }}>
        <Typography variant="caption" color="text.secondary">
          Shared grammar: 8px status dot (the word reaches screen readers via
          the row label and shows in the collapsed meta line), monospace refs,
          tabular-nums amounts, cancelled rows dimmed and struck, deductions in
          red. Toggle dark/light in Settings to check both modes.
        </Typography>
      </Box>
    </Container>
  );
}
